/** @jsxImportSource @emotion/react */
import { createStyles, Loader, Typography } from '@quickbit/qb-design-system';
import { useEffect, useRef } from 'react';
import { ChatEvent as ChatMessageType } from 'api/useAgentChatApi';
import { ChatMessage } from './ChatMessage';

interface MessageListProps {
  messages: ChatMessageType[];
  isLoading: boolean;
  isPending?: boolean;
}

export const MessageList = ({
  messages,
  isLoading,
  isPending = false,
}: MessageListProps) => {
  const styles = useStyles();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isPending]);

  if (isLoading) {
    return (
      <div css={styles.loadingContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <div css={styles.container}>
      {!isPending && messages.length === 0 && (
        <div css={styles.emptyContainer}>
          <Typography variant="body">
            No messages yet. Start a conversation!
          </Typography>
        </div>
      )}
      {messages.map((msg) => (
        <ChatMessage
          key={msg.id}
          eventType={msg.eventType}
          content={msg.content}
          timestamp={msg.createdAt}
        />
      ))}
      {isPending && (
        <div css={styles.pendingIndicator}>
          <Loader />
          <Typography variant="caption">Agent is thinking...</Typography>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    flex: 1,
    overflowY: 'auto',
    padding: spacing.xs,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  emptyContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  pendingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xxs,
    alignSelf: 'flex-start',
    padding: spacing.xxs,
  },
}));
